import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, IconButton, Paper } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "contexts/ToastContext";
import {
  useGetMedicinesInPrescription,
  useAddMedicinesToPrescription,
  useRemoveMedicineFromPrescription,
} from "hooks/useMedicine";
import { MedicineResponseDto } from "libs/cellera-sdk";
import { useCallback, useEffect, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { MedicineWithOrderDetails } from "types/medicine";

import ConfirmDeleteModal from "./ConfirmDeleteModal";
import EmptyMedicine from "./EmptyMedicine";
import LoadingMedicine from "./LoadingMedicine";
import MedicineCreator from "./MedicineCreator";
import MedicineTable from "./MedicineTable";

interface MedicineProps {
  prescriptionId?: string;
  onMedicinesChange?: (medicines: MedicineResponseDto[]) => void;
  onCreatePrescription?: () => Promise<string | undefined>;
  isSaving?: boolean;
}

function Medicine({
  prescriptionId,
  onMedicinesChange,
  onCreatePrescription,
  isSaving = false,
}: MedicineProps) {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const [showMedicineCreator, setShowMedicineCreator] = useState(false);
  const [medicinesWithDetails, setMedicinesWithDetails] = useState<
    MedicineWithOrderDetails[]
  >([]);
  const [medicineToDelete, setMedicineToDelete] =
    useState<MedicineWithOrderDetails | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [currentPrescriptionId, setCurrentPrescriptionId] = useState<
    string | undefined
  >(prescriptionId);

  const queryClient = useQueryClient();

  const { data: medicinesInPrescriptionData, isLoading } =
    useGetMedicinesInPrescription(currentPrescriptionId);

  // Update currentPrescriptionId when prescriptionId prop changes
  useEffect(() => {
    if (prescriptionId) {
      setCurrentPrescriptionId(prescriptionId);
    }
  }, [prescriptionId]);

  const {
    mutateAsync: addMedicinesToPrescription,
    isPending: isAddingMedicine,
  } = useAddMedicinesToPrescription();

  const {
    mutateAsync: removeMedicineFromPrescription,
    isPending: isRemovingMedicine,
  } = useRemoveMedicineFromPrescription();

  const handleAddNewMedicine = useCallback(() => {
    setShowMedicineCreator(true);
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  }, []);

  const handleCloseMedicineCreator = useCallback(() => {
    setShowMedicineCreator(false);
  }, []);

  const handleConfirmMedicines = useCallback(
    async (selectedMedicines: MedicineWithOrderDetails[]) => {
      // Close the medicine creator and show loading immediately
      setShowMedicineCreator(false);
      setIsAdding(true);

      let targetPrescriptionId = currentPrescriptionId;
      const previousMedicines: MedicineWithOrderDetails[] = [
        ...medicinesWithDetails,
      ];

      try {
        // If no prescription exists, create one first
        if (!targetPrescriptionId && onCreatePrescription) {
          try {
            targetPrescriptionId = await onCreatePrescription();
            setCurrentPrescriptionId(targetPrescriptionId);
          } catch (error) {
            console.error("Failed to create prescription:", error);
            setIsAdding(false);
            throw error;
          }
        }

        // Extract medicine items with order details
        const medicineItems = selectedMedicines.map(
          (item) => item.orderDetails
        );

        // Optimistically add to local state immediately
        setMedicinesWithDetails((prev) => [...selectedMedicines, ...prev]);
        setIsAdding(false); // Hide loading immediately

        // If we have a prescription, add the medicines to it in the background
        if (targetPrescriptionId) {
          try {
            await addMedicinesToPrescription({
              prescriptionId: targetPrescriptionId,
              medicines: medicineItems,
            });
            // Refetch to ensure data is in sync
            queryClient.invalidateQueries({
              queryKey: ["medicinesInPrescription"],
            });
          } catch (error) {
            // Rollback optimistic update on error
            setMedicinesWithDetails(previousMedicines);
            console.error("Failed to add medicines to prescription:", error);
            showError(
              "Add Medicines Failed",
              error instanceof Error
                ? error.message
                : "Failed to add medicines to prescription. Please try again."
            );
            throw error;
          }
        }
      } catch (error) {
        // Rollback optimistic update on error
        setMedicinesWithDetails(previousMedicines);
        setIsAdding(false);
        throw error;
      }
    },
    [
      currentPrescriptionId,
      onCreatePrescription,
      addMedicinesToPrescription,
      queryClient,
      showError,
      medicinesWithDetails,
    ]
  );

  const handleOpenDeleteModal = useCallback(
    (medicine: MedicineWithOrderDetails) => {
      setMedicineToDelete(medicine);
    },
    []
  );

  const handleCloseDeleteModal = useCallback(() => {
    setMedicineToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!medicineToDelete) return;

    setIsDeleting(true);

    // Save previous state for rollback
    const previousMedicines = [...medicinesWithDetails];
    const deletedMedicine = medicineToDelete;

    // Optimistically remove from local state immediately
    setMedicinesWithDetails((prev) =>
      prev.filter((m) => m.medicine.id !== deletedMedicine.medicine.id)
    );
    handleCloseDeleteModal();
    setIsDeleting(false); // Hide loading immediately

    try {
      if (currentPrescriptionId) {
        await removeMedicineFromPrescription({
          prescriptionId: currentPrescriptionId,
          medicineId: deletedMedicine.medicine.id,
        });
        // Refetch to ensure data is in sync
        queryClient.invalidateQueries({
          queryKey: ["medicinesInPrescription"],
        });
      }
    } catch (error) {
      // Rollback optimistic update on error
      setMedicinesWithDetails(previousMedicines);
      console.error("Error deleting medicine:", error);
      showError(
        "Delete Medicine Failed",
        error instanceof Error
          ? error.message
          : "Failed to delete medicine. Please try again."
      );
    }
  }, [
    medicineToDelete,
    currentPrescriptionId,
    queryClient,
    removeMedicineFromPrescription,
    handleCloseDeleteModal,
    medicinesWithDetails,
    showError,
  ]);

  useEffect(() => {
    if (medicinesInPrescriptionData?.medicines) {
      // Convert API response (MedicineInOrderResponseDto[]) to MedicineWithOrderDetails format
      const medicinesWithDetailsFromApi: MedicineWithOrderDetails[] =
        medicinesInPrescriptionData.medicines.map((item) => ({
          medicine: item.medicine,
          orderDetails: {
            medicineId: item.medicine.id,
            sig: item.sig,
            dispense: item.dispense,
            refill: item.refill,
          },
        }));
      setMedicinesWithDetails(medicinesWithDetailsFromApi);
    }
  }, [medicinesInPrescriptionData]);

  useEffect(() => {
    if (onMedicinesChange) {
      const medicines = medicinesWithDetails.map((item) => item.medicine);
      onMedicinesChange(medicines);
    }
  }, [medicinesWithDetails, onMedicinesChange]);

  return (
    <Box sx={{ width: "100%" }} className="space-y-5">
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border.main}`,
          borderRadius: 1,
          overflow: "hidden",
          maxHeight: "550px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MedicineTable
          medicines={medicinesWithDetails}
          loadingState={
            isLoading || isAdding || isAddingMedicine ? (
              <LoadingMedicine />
            ) : undefined
          }
          emptyState={
            medicinesWithDetails?.length === 0 ? (
              <EmptyMedicine onCreateMedicine={handleAddNewMedicine} />
            ) : undefined
          }
          renderActions={(item) => (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                justifyContent: "center",
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleOpenDeleteModal(item)}
                disabled={isSaving || isDeleting || isRemovingMedicine}
                sx={{
                  color: colors.error[500],
                  padding: 0.5,
                  "&:hover": {
                    backgroundColor: colors.error[100],
                  },
                  "&:disabled": {
                    color: colors.text.secondary,
                    opacity: 0.5,
                  },
                }}
                title="Delete"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          cellPadding={1}
          sigMaxWidth={300}
          actionsCellWidth="120px"
        />

        {!!medicinesWithDetails?.length && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              borderTop: `1px solid ${colors.border.main}`,
              minHeight: "52px",
              height: "52px",
            }}
          >
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddNewMedicine}
              sx={{
                color: colors.secondary.main,
                textTransform: "none",
                fontWeight: 500,
                py: 0.5,
              }}
            >
              Add New Item
            </Button>
          </Box>
        )}
      </Paper>

      {showMedicineCreator && (
        <MedicineCreator
          onClose={handleCloseMedicineCreator}
          isAdding={isAddingMedicine}
          onConfirm={handleConfirmMedicines}
        />
      )}

      <ConfirmDeleteModal
        open={!!medicineToDelete}
        medicineName={
          medicineToDelete?.medicine.brandName ||
          medicineToDelete?.medicine.name ||
          ""
        }
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting || isRemovingMedicine}
      />
    </Box>
  );
}

export default Medicine;
